import React from 'react';

interface LoadingProps {
  /** Display text under the spinner */
  message?: string;
  /** Size of the spinner in pixels */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to take full page height */
  fullPage?: boolean;
  /** Type of skeleton layout to show */
  variant?: 'spinner' | 'page' | 'chart' | 'table';
}

/**
 * Loading component for use as Suspense fallback during code splitting.
 * Supports both dark and light mode themes.
 */
const Loading: React.FC<LoadingProps> = ({
  message = 'Loading...',
  size = 'md',
  fullPage = false,
  variant = 'spinner',
}) => {
  const sizeMap = {
    sm: 'h-6 w-6',
    md: 'h-10 w-10',
    lg: 'h-16 w-16',
  };

  const spinnerSize = sizeMap[size];

  if (variant === 'page') {
    return (
      <div
        className={`flex flex-col items-center justify-center ${
          fullPage ? 'min-h-screen' : 'min-h-[400px]'
        } bg-white dark:bg-dark-bg`}
        data-testid="loading-page"
      >
        <div className="space-y-4 w-full max-w-4xl px-4">
          {/* Header skeleton */}
          <div className="h-8 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />

          {/* Stats row skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="h-24 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
              />
            ))}
          </div>

          {/* Chart skeleton */}
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />

          {/* Table skeleton */}
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'chart') {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[300px] bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4"
        data-testid="loading-chart"
      >
        <div className="w-full space-y-3">
          {/* Chart header */}
          <div className="flex justify-between items-center">
            <div className="h-5 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="flex gap-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-6 w-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
                />
              ))}
            </div>
          </div>
          {/* Chart area */}
          <div className="h-56 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (variant === 'table') {
    return (
      <div
        className="bg-white dark:bg-dark-card rounded-lg border border-gray-200 dark:border-dark-border p-4"
        data-testid="loading-table"
      >
        <div className="space-y-3">
          {/* Table header */}
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          {/* Table rows */}
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-12 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  // Default spinner variant
  return (
    <div
      className={`flex flex-col items-center justify-center ${
        fullPage ? 'min-h-screen' : 'min-h-[200px]'
      } bg-white dark:bg-dark-bg`}
      data-testid="loading-spinner"
    >
      <div
        className={`${spinnerSize} border-4 border-gray-200 dark:border-gray-700 border-t-blue-600 dark:border-t-blue-500 rounded-full animate-spin`}
        role="status"
        aria-label="Loading"
      />
      {message && (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{message}</p>
      )}
    </div>
  );
};

export default Loading;
